import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import type { CleanupContactInfo } from "../services/ansprechpartnerCleanupService";

const MUTED = "#6b5a66";
const INK = "#241522";
const LINE = "#ECE7F0";

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "18px",
    rowGap: "6px",
    ...shorthands.padding("8px", "12px"),
    backgroundColor: "#faf8fb",
    ...shorthands.border("1px", "solid", LINE),
    ...shorthands.borderRadius("8px"),
  },
  item: { display: "flex", flexDirection: "column", rowGap: "1px", minWidth: "0" },
  lbl: { fontSize: "0.66rem", fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.04em" },
  val: { fontSize: "0.84rem", color: INK },
});

const COMM_LABEL: Record<string, string> = {
  phone: "Telefon",
  mobile: "Mobil",
  fax: "Fax",
  email: "E-Mail",
  homepage: "Web",
  linkedin: "LinkedIn",
  xing: "Xing",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
};

const fmtDate = (d?: string | null): string | null => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("de-DE");
};

/** Kompakte Read-only-Vorschau der vorhandenen Kontaktstammdaten (nur befüllte Felder). */
export function CleanupContactPreview({ info }: { info: CleanupContactInfo }) {
  const s = useStyles();

  const items: Array<{ lbl: string; val: string }> = [];
  const push = (lbl: string, val?: string | null) => {
    const v = (val ?? "").toString().trim();
    if (v) items.push({ lbl, val: v });
  };

  push("Anrede", info.salutationLabel);
  push("Titel", info.titleLabel);
  push("Rufname", info.callName);
  push("Geburtsdatum", fmtDate(info.birthDate));
  push("Geschlecht", info.gender);
  push("Geburtsort", info.birthPlace);
  push("Nationalität", info.nationality);
  push("Familienstand", info.maritalStatus);
  push("Berufsstatus", info.jobStatus);
  push("Tätigkeit", info.jobOccupation);
  push("Branche", info.jobIndustry);
  push("Arbeitgeber", info.jobEmployer);
  push("Erreichbarkeit", info.erreichbarkeit);
  push("Bemerkung", info.remark);

  const addr = info.address;
  const addrStr = addr
    ? [addr.street, [addr.postalCode, addr.city].filter(Boolean).join(" ")].filter((x) => x && x.trim()).join(", ")
    : "";
  if (addrStr.trim()) items.push({ lbl: "Adresse", val: addrStr });

  for (const c of info.communications ?? []) {
    if (c.value?.trim()) items.push({ lbl: COMM_LABEL[c.type] ?? c.type, val: c.value });
  }

  if (items.length === 0) return null;

  return (
    <div className={s.wrap}>
      {items.map((it, i) => (
        <div key={`${it.lbl}-${i}`} className={s.item}>
          <span className={s.lbl}>{it.lbl}</span>
          <span className={s.val}>{it.val}</span>
        </div>
      ))}
    </div>
  );
}
